import { Edit, Trash2, FileText, User, Calendar, MapPin, CreditCard, CheckCircle, XCircle, Clock, AlertCircle, Download } from 'lucide-react'
import { LoadingSpinner } from '../common'

function InvoiceList({ 
  invoices, 
  isLoading, 
  onEdit, 
  onDelete,
  onDownloadPDF 
}) {
  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case 'Paid':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'Partial':
        return <Clock className="h-4 w-4 text-yellow-600" />
      case 'Overdue':
        return <AlertCircle className="h-4 w-4 text-red-600" />
      case 'Pending':
        return <Clock className="h-4 w-4 text-gray-600" />
      default:
        return <Clock className="h-4 w-4 text-gray-600" />
    }
  }

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800'
      case 'Partial':
        return 'bg-yellow-100 text-yellow-800'
      case 'Overdue':
        return 'bg-red-100 text-red-800'
      case 'Pending':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800'
      case 'In Progress':
        return 'bg-blue-100 text-blue-800'
      case 'Cancelled':
        return 'bg-red-100 text-red-800'
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-content p-0">
          <LoadingSpinner size="lg" className="h-64" />
        </div>
      </div>
    )
  }

  if (!invoices || invoices.length === 0) {
    return (
      <div className="card">
        <div className="card-content">
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-base font-medium text-secondary-900">No invoices found</h3>
            <p className="mt-1 text-base text-secondary-500">Get started by creating a new invoice.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-content p-0">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr className="table-row">
                <th className="table-head">Invoice</th>
                <th className="table-head">Customer</th>
                <th className="table-head">Payment Status</th>
                <th className="table-head">Order Status</th>
                <th className="table-head">Amount</th>
                <th className="table-head">Delivery Date</th>
                <th className="table-head">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="table-row">
                  <td className="table-cell">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <FileText className="h-4 w-4 text-primary-600" />
                      </div>
                      <div className="ml-3">
                        <div className="text-base font-medium text-secondary-900">
                          Invoice #{invoice.id}
                        </div>
                        {invoice.notes && (
                          <div className="text-base text-secondary-500 truncate max-w-xs">
                            {invoice.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    {invoice.customer ? (
                      <div className="flex items-center">
                        <div className="h-6 w-6 bg-secondary-100 rounded-full flex items-center justify-center">
                          <User className="h-3 w-3 text-secondary-600" />
                        </div>
                        <div className="ml-2">
                          <div className="text-base font-medium text-secondary-900">
                            {invoice.customer.firstName} {invoice.customer.lastName}
                          </div>
                          {invoice.customer.phone && (
                            <div className="text-base text-secondary-500">
                              {invoice.customer.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-base text-secondary-500">N/A</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center">
                      {getPaymentStatusIcon(invoice.paymentStatus)}
                      <span className={`ml-1 badge ${getPaymentStatusColor(invoice.paymentStatus)}`}>
                        {invoice.paymentStatus}
                      </span>
                    </div>
                  </td>
                  <td className="table-cell">
                    <span className={`badge ${getOrderStatusColor(invoice.orderStatus)}`}>
                      {invoice.orderStatus}
                    </span>
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center">
                      <CreditCard className="h-4 w-4 text-secondary-400 mr-1" />
                      <div>
                        <div className="text-sm font-medium text-secondary-900">
                          ₹{parseFloat(invoice.totalAmount || 0).toFixed(2)}
                        </div>
                        {invoice.taxPercentage > 0 && (
                          <div className="text-xs text-secondary-500">
                            Tax: {invoice.taxPercentage}%
                          </div>
                        )}
                        {invoice.discountPercentage > 0 && (
                          <div className="text-xs text-secondary-500">
                            Discount: {invoice.discountPercentage}%
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    {invoice.deliveryDate ? (
                      <div className="flex items-center text-sm text-secondary-900">
                        <Calendar className="h-4 w-4 mr-1 text-secondary-400" />
                        {new Date(invoice.deliveryDate).toLocaleDateString()}
                      </div>
                    ) : (
                      <span className="text-sm text-secondary-500">Not set</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <div className="flex items-center space-x-2">
                      {onDownloadPDF && (
                        <button
                          onClick={() => onDownloadPDF(invoice.id)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Download PDF"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onEdit(invoice)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete(invoice)}
                        className="text-danger-600 hover:text-danger-900"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default InvoiceList

